import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * AdScriptLoader Component
 * Fetches and injects ad scripts from the super admin configured scripts
 * 
 * Props:
 * - page: 'landing' | 'chat' | 'about' | 'contact' (lowercase)
 * - position: 'header' | 'footer' | 'sidebar'
 * - user: current user object (to check premium status)
 * - className: optional CSS class for the container
 */
const AdScriptLoader = ({ page, position, user, className = '' }) => {
  const [adScripts, setAdScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adVersion, setAdVersion] = useState(null);
  const containerRef = useRef(null);
  const mountedRef = useRef(true);
  const versionCheckIntervalRef = useRef(null);

  // Function to check for version updates
  const checkVersion = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/system/ad-version`);
      const newVersion = response.data.version;
      
      if (adVersion !== null && newVersion !== adVersion) {
        // Version changed, reload scripts
        console.log('Ad scripts version changed, reloading...');
        loadAdScripts();
      }
      
      if (mountedRef.current) {
        setAdVersion(newVersion);
      }
    } catch (error) {
      console.error('Failed to check ad version:', error);
    }
  }, [adVersion]);

  // Function to load ad scripts
  const loadAdScripts = useCallback(async () => {
    // Premium users don't see ads
    if (user && user.role === 'premium') {
      setAdScripts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const pageName = (page && typeof page.toLowerCase === 'function' ? page.toLowerCase() : page) || 'chat';
      const response = await axios.get(`${API}/ad-scripts/${pageName}`);
      
      if (!mountedRef.current) return;
      
      // Filter by position
      const filteredScripts = (response.data || []).filter(
        script => script.position === position
      );
      setAdScripts(filteredScripts);
    } catch (error) {
      console.error(`Failed to load ad scripts for ${page}/${position}:`, error);
      if (mountedRef.current) {
        setAdScripts([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [page, position, user]);

  // Load scripts on mount and start version polling
  useEffect(() => {
    mountedRef.current = true;
    loadAdScripts();
    checkVersion();
    
    // Poll for version changes every 30 seconds
    versionCheckIntervalRef.current = setInterval(checkVersion, 30000);
    
    return () => {
      mountedRef.current = false;
      if (versionCheckIntervalRef.current) {
        clearInterval(versionCheckIntervalRef.current);
      }
    };
  }, [loadAdScripts, checkVersion]);

  // Inject scripts when they change
  useEffect(() => {
    const container = containerRef.current;
    if (!container || adScripts.length === 0) return;
    
    injectScripts();
    
    // Cleanup on unmount or when scripts change
    return () => {
      if (container) {
        // Remove any dynamically added script elements
        const scripts = container.querySelectorAll('script');
        scripts.forEach(script => script.remove());
        container.innerHTML = '';
      }
    };
  }, [adScripts]);

  const injectScripts = () => {
    const container = containerRef.current;
    if (!container) return;
    
    // Clear container
    container.innerHTML = '';
    
    adScripts.forEach((script, index) => {
      try {
        const wrapper = document.createElement('div');
        wrapper.className = 'ad-script-item';
        wrapper.setAttribute('data-ad-index', index);
        
        const scriptContent = script.script_code;
        
        if (typeof scriptContent === 'string') {
          // Handle script tags
          if (scriptContent.includes('<script')) {
            // Create temporary container to parse
            const temp = document.createElement('div');
            temp.innerHTML = scriptContent;
            
            // Process script tags
            temp.querySelectorAll('script').forEach(oldScript => {
              const newScript = document.createElement('script');
              
              // Copy all attributes
              Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
              });
              
              // Copy content
              if (oldScript.innerHTML) {
                newScript.textContent = oldScript.innerHTML;
              }
              
              wrapper.appendChild(newScript);
            });
            
            // Add non-script content (like AdSense ins tags)
            const htmlWithoutScripts = scriptContent.replace(/<script[\s\S]*?<\/script>/gi, '');
            if (htmlWithoutScripts.trim()) {
              const contentWrapper = document.createElement('div');
              contentWrapper.innerHTML = htmlWithoutScripts;
              wrapper.insertBefore(contentWrapper, wrapper.firstChild);
            }
          } else {
            // Plain HTML content
            wrapper.innerHTML = scriptContent;
          }
        } else {
          console.warn(`Ad script content for index ${index} is not a string, skipping injection:`, scriptContent);
        }
        
        container.appendChild(wrapper);
      } catch (error) {
        console.error(`Failed to inject ad script ${index}:`, error);
      }
    });
  };

  // Don't render for premium users or when no scripts
  if (user && user.role === 'premium') {
    return null;
  }

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (adScripts.length === 0) {
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className={`ad-script-container ad-position-${position} ${className}`}
      data-page={page}
      data-position={position}
    />
  );
};

export default AdScriptLoader;
